import { WorkspaceEditorState } from "../../../types";
import {
  WorkspaceFileList,
  type WorkspaceFileListEntry,
  type WorkspaceFileListSection,
} from "./WorkspaceFileList";

/**
 * The code workspace's file tree, as rows over {@link WorkspaceFileList}. Top
 * to bottom — the locked contract file, any other locked read-only file (e.g.
 * Agentic Logic's `sdk.d.ts`), the fixed entry file, then each user helper file
 * (with rename/delete affordances), plus an "Add file" action. The ambient
 * logger module is never shown. Add/rename/delete are suppressed when the panel
 * is read-only, and hidden entirely for single-file domains
 * (`editor.allowUserFiles === false`).
 *
 * A domain with editor test files gets a "Tests" group below: the author's own
 * suite, then the AI-generated one, locked because a generated test becomes
 * editable only by moving it into the author's file.
 */
export const FileSidebar = ({
  editor,
  readOnly,
}: {
  editor: WorkspaceEditorState;
  readOnly: boolean;
}) => {
  const userFiles = Object.keys(editor.files)
    .filter((name) => name !== editor.entryFile)
    .sort();
  const canManageAny = !readOnly && editor.allowUserFiles;

  const files: WorkspaceFileListEntry[] = [
    { name: editor.typesFile, isLocked: true },
    ...editor.readonlyFiles.map((name) => ({ name, isLocked: true })),
    { name: editor.entryFile, isLocked: false },
    ...userFiles.map((name) => ({
      name,
      isLocked: false,
      canManage: canManageAny,
    })),
  ];

  const sections: WorkspaceFileListSection[] =
    editor.testFiles === null
      ? []
      : [
          {
            label: "Tests",
            files: [
              {
                name: editor.testFiles.user.name,
                isLocked: false,
                kind: "test",
              },
              {
                name: editor.testFiles.generated.name,
                isLocked: true,
                kind: "test",
                tag: "AI",
              },
            ],
          },
        ];

  return (
    <WorkspaceFileList
      files={files}
      activeFile={editor.activeFile}
      onSelect={editor.setActiveFile}
      onAdd={canManageAny ? editor.addFile : undefined}
      onRename={editor.renameFile}
      onDelete={editor.deleteFile}
      sections={sections}
    />
  );
};
